import { ReactElement, useState } from 'react'
import type { DocumentResult } from '@creanizer/types'
import { Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Label } from '@creanizer/ui'
import { Download, FileJson, FileText, FileCode, File } from 'lucide-react'

interface DocumentExporterProps {
  document: DocumentResult
  trigger?: ReactElement
}

type ExportFormat = 'json' | 'text' | 'markdown' | 'html'

export function DocumentExporter({ document: doc, trigger }: DocumentExporterProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [format, setFormat] = useState<ExportFormat>('text')
  const [showOptions, setShowOptions] = useState(false)
  
  // テキスト形式へのエクスポート
  const exportAsText = (): string => {
    let text = ''
    
    switch (doc.type) {
      case 'newspaper':
        const newspaper = doc as any
        text = `${newspaper.publication || '新聞'}\n`
        text += `${newspaper.date}\n`
        text += '='.repeat(50) + '\n\n'
        text += `【${newspaper.headline}】\n\n`
        if (newspaper.subheadline) {
          text += `${newspaper.subheadline}\n\n`
        }
        if (newspaper.lead) {
          text += `${newspaper.lead}\n\n`
        }
        text += newspaper.body
        if (newspaper.author) {
          text += `\n\n記者: ${newspaper.author.name}`
        }
        break
        
      case 'sns_twitter':
        const twitter = doc as any
        twitter.posts?.forEach((post: any, index: number) => {
          if (index > 0) text += '\n---\n\n'
          text += `@${post.author.username} (${post.author.displayName})\n`
          text += `${post.timestamp}\n\n`
          text += `${post.text}\n\n`
          text += `❤️ ${post.engagement.likes} | 🔄 ${post.engagement.retweets} | 💬 ${post.engagement.replies}`
        })
        break
        
      case 'diary':
        const diary = doc as any
        text = `日記\n${diary.date}\n`
        if (diary.weather) text += `天気: ${diary.weather}\n`
        if (diary.mood) text += `気分: ${diary.mood}\n`
        text += '\n' + diary.body
        break
        
      default:
        text = JSON.stringify(doc, null, 2)
    }
    
    return text
  }
  
  // Markdown形式へのエクスポート
  const exportAsMarkdown = (): string => {
    let md = ''
    
    switch (doc.type) {
      case 'newspaper':
        const newspaper = doc as any
        md = `# ${newspaper.headline}\n\n`
        if (newspaper.subheadline) {
          md += `## ${newspaper.subheadline}\n\n`
        }
        md += `*${newspaper.publication} - ${newspaper.date}*\n\n`
        if (newspaper.lead) {
          md += `**${newspaper.lead}**\n\n`
        }
        md += newspaper.body
        if (newspaper.author) {
          md += `\n\n---\n*記者: ${newspaper.author.name}*`
        }
        break
        
      case 'blog':
        const blog = doc as any
        md = `# ${blog.title}\n\n`
        md += `*${blog.date} - ${blog.author.name}*\n\n`
        if (blog.categories?.length > 0) {
          md += `カテゴリ: ${blog.categories.join(', ')}\n\n`
        }
        if (blog.summary) {
          md += `> ${blog.summary}\n\n`
        }
        md += blog.body
        break
        
      default:
        md = exportAsText()
    }
    
    return md
  }
  
  // HTML形式へのエクスポート
  const exportAsHtml = (): string => {
    const content = exportAsMarkdown()
    // 簡易的なMarkdown to HTML変換
    let html = `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Document Export</title>
  <style>
    body { font-family: sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
    h1 { color: #333; }
    h2 { color: #666; }
    blockquote { border-left: 3px solid #ccc; padding-left: 1em; color: #666; }
  </style>
</head>
<body>
`
    
    // 簡易変換
    html += content
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$2</h2>')
      .replace(/^\*(.+)\*$/gm, '<em>$1</em>')
      .replace(/^\*\*(.+)\*\*$/gm, '<strong>$1</strong>')
      .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
      .replace(/\n/g, '<br>\n')
    
    html += '\n</body>\n</html>'
    return html
  }
  
  // エクスポート実行
  const handleExport = () => {
    setIsExporting(true)
    
    let content = ''
    let mimeType = ''
    let extension = ''
    
    switch (format) {
      case 'json':
        content = JSON.stringify(doc, null, 2)
        mimeType = 'application/json'
        extension = 'json'
        break
      case 'text':
        content = exportAsText()
        mimeType = 'text/plain'
        extension = 'txt'
        break
      case 'markdown':
        content = exportAsMarkdown()
        mimeType = 'text/markdown'
        extension = 'md'
        break
      case 'html':
        content = exportAsHtml()
        mimeType = 'text/html'
        extension = 'html'
        break
    }
    
    // ダウンロード実行
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `document_${doc.type}_${Date.now()}.${extension}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    setIsExporting(false)
    setShowOptions(false)
  }
  
  // カスタムトリガーがある場合
  if (trigger) {
    return (
      <>
        <div onClick={() => setShowOptions(true)}>
          {trigger}
        </div>
        
        {showOptions && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">エクスポート設定</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="format">エクスポート形式</Label>
                  <Select
                    value={format}
                    onValueChange={(value) => setFormat(value as ExportFormat)}
                  >
                    <SelectTrigger id="format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">テキスト (.txt)</SelectItem>
                      <SelectItem value="markdown">Markdown (.md)</SelectItem>
                      <SelectItem value="html">HTML (.html)</SelectItem>
                      <SelectItem value="json">JSON (.json)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowOptions(false)}
                  >
                    キャンセル
                  </Button>
                  <Button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    エクスポート
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }
  
  // デフォルトボタン
  return (
    <Button
      onClick={handleExport}
      disabled={isExporting}
      variant="outline"
      size="sm"
      className="flex items-center gap-2"
    >
      <Download className="w-4 h-4" />
      エクスポート
    </Button>
  )
}